import type { Variants, Transition } from 'framer-motion'

// ============================================
// EASING CURVES - Natural, smooth easings
// ============================================

export const easings = {
  // Smooth ease out - great for entrances
  easeOutSmooth: [0.16, 1, 0.3, 1] as const,
  // Gentle ease in-out - for balanced motion
  easeInOutSmooth: [0.4, 0, 0.2, 1] as const,
  // Quick ease out - for snappy interactions
  easeOutQuick: [0.25, 0.46, 0.45, 0.94] as const,
  // Bounce ease out - for playful elements
  easeOutBounce: [0.68, -0.55, 0.265, 1.55] as const,
  // Expo ease out - for dramatic reveals
  easeOutExpo: [0.19, 1, 0.22, 1] as const,
  // Spring-like ease - natural movement
  easeSpring: [0.175, 0.885, 0.32, 1.275] as const,
} as const

// ============================================
// TRANSITION PRESETS
// ============================================

export const transitions = {
  // Fast - for hover states, quick feedback
  fast: {
    duration: 0.15,
    ease: easings.easeOutQuick,
  } as Transition,
  
  // Normal - for standard animations
  normal: {
    duration: 0.3,
    ease: easings.easeOutSmooth,
  } as Transition,
  
  // Slow - for page transitions, important reveals
  slow: {
    duration: 0.5,
    ease: easings.easeOutSmooth,
  } as Transition,
  
  // Stagger - for list animations
  stagger: {
    staggerChildren: 0.05,
    delayChildren: 0.1,
  } as Transition,
  
  // Stagger slow - for more dramatic lists
  staggerSlow: {
    staggerChildren: 0.08,
    delayChildren: 0.15,
  } as Transition,
} as const

// ============================================
// PAGE TRANSITIONS
// ============================================

export const pageTransitions = {
  // Fade in from bottom - standard page entrance
  fadeInUp: {
    initial: false as unknown as { opacity: number; y: number },
    animate: { 
      opacity: 1, 
      y: 0,
      transition: transitions.slow
    },
  } as Variants,
  
  // Fade in - simple entrance
  fadeIn: {
    initial: false as unknown as { opacity: number },
    animate: { 
      opacity: 1,
      transition: transitions.normal
    },
  } as Variants,
  
  // Scale fade - for modals, cards
  scaleFade: {
    initial: false as unknown as { opacity: number; scale: number },
    animate: { 
      opacity: 1, 
      scale: 1,
      transition: {
        duration: 0.25,
        ease: easings.easeSpring,
      }
    },
  } as Variants,
  
  // Slide in from left
  slideInLeft: {
    initial: false as unknown as { opacity: number; x: number },
    animate: { 
      opacity: 1, 
      x: 0,
      transition: transitions.normal
    },
  } as Variants,
  
  // Slide in from right
  slideInRight: {
    initial: false as unknown as { opacity: number; x: number },
    animate: { 
      opacity: 1, 
      x: 0,
      transition: transitions.normal
    },
  } as Variants,
} as const

// ============================================
// COMPONENT ANIMATIONS
// ============================================

export const componentAnimations = {
  // Card hover effect
  cardHover: {
    rest: { y: 0, scale: 1 },
    hover: { 
      y: -4, 
      scale: 1.01,
      transition: transitions.fast
    },
    tap: { 
      scale: 0.98,
      transition: { duration: 0.1 }
    },
  } as Variants,
  
  // Button press effect
  buttonPress: {
    rest: { scale: 1 },
    hover: { scale: 1.02 },
    tap: { scale: 0.97 },
  } as Variants,
  
  // Icon rotation on hover
  iconRotate: {
    rest: { rotate: 0 },
    hover: { 
      rotate: 15,
      transition: transitions.fast
    },
  } as Variants,
  
  // List item entrance
  listItem: {
    hidden: { opacity: 0, x: -10 },
    visible: { 
      opacity: 1, 
      x: 0,
      transition: transitions.normal
    },
  } as Variants,
  
  // Stagger container for lists
  staggerContainer: {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: transitions.stagger,
    },
  } as Variants,
  
  // Stagger container slow
  staggerContainerSlow: {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: transitions.staggerSlow,
    },
  } as Variants,
} as const

// ============================================
// MODAL & OVERLAY ANIMATIONS
// ============================================

export const modalAnimations = {
  // Overlay fade
  overlay: {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { duration: 0.2 }
    },
    exit: { 
      opacity: 0,
      transition: { duration: 0.15 }
    },
  } as Variants,
  
  // Modal content scale + fade
  content: {
    hidden: { opacity: 0, scale: 0.9, y: 20 },
    visible: { 
      opacity: 1, 
      scale: 1, 
      y: 0,
      transition: {
        duration: 0.25,
        ease: easings.easeSpring,
      }
    },
    exit: { 
      opacity: 0, 
      scale: 0.9, 
      y: 20,
      transition: { duration: 0.15 }
    },
  } as Variants,
  
  // Dialog header/footer
  dialogSection: {
    hidden: { opacity: 0, y: -10, scale: 0.95 },
    visible: { 
      opacity: 1, 
      y: 0, 
      scale: 1,
      transition: {
        duration: 0.2,
        ease: easings.easeOutSmooth,
      }
    },
    exit: { 
      opacity: 0, 
      y: -10, 
      scale: 0.95,
      transition: { duration: 0.15 }
    },
  } as Variants,
} as const

// ============================================
// SIDEBAR ANIMATIONS
// ============================================

export const sidebarAnimations = {
  // Sidebar slide
  sidebar: {
    open: {
      x: 0,
      transition: {
        duration: 0.25,
        ease: easings.easeOutExpo,
      }
    },
    closed: {
      x: -280,
      transition: {
        duration: 0.2,
        ease: easings.easeInOutSmooth,
      }
    },
  } as Variants,
  
  // Nav link hover
  navLink: {
    rest: { x: 0 },
    hover: { 
      x: 4,
      transition: transitions.fast
    },
    tap: { 
      scale: 0.98,
      transition: { duration: 0.1 }
    },
  } as Variants,
} as const

// ============================================
// STATS & DATA DISPLAY ANIMATIONS
// ============================================

export const statsAnimations = {
  // Stats card entrance
  statsCard: {
    hidden: { opacity: 0, y: 20, scale: 0.95 },
    visible: { 
      opacity: 1, 
      y: 0, 
      scale: 1,
      transition: {
        duration: 0.35,
        ease: easings.easeSpring,
      }
    },
  } as Variants,
  
  // Counter animation container
  counter: {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { duration: 0.3 }
    },
  } as Variants,
  
  // Tree item (for referrals, etc.)
  treeItem: {
    hidden: { opacity: 0, x: -15 },
    visible: { 
      opacity: 1, 
      x: 0,
      transition: transitions.normal
    },
  } as Variants,
} as const

// ============================================
// LOADING & FEEDBACK ANIMATIONS
// ============================================

export const feedbackAnimations = {
  // Spinner continuous rotation
  spinner: {
    animate: { 
      rotate: 360,
      transition: {
        duration: 1,
        repeat: Infinity,
        ease: 'linear',
      }
    },
  } as Variants,
  
  // Success checkmark draw
  checkmark: {
    hidden: { pathLength: 0, opacity: 0 },
    visible: {
      pathLength: 1,
      opacity: 1,
      transition: {
        pathLength: { duration: 0.4, ease: easings.easeOutSmooth },
        opacity: { duration: 0.2 },
      }
    },
  } as Variants,
  
  // Error shake
  shake: {
    shake: {
      x: [-8, 8, -8, 8, 0],
      transition: { duration: 0.4 }
    },
  } as Variants,
  
  // Pulse for attention
  pulse: {
    animate: {
      scale: [1, 1.05, 1],
      transition: {
        duration: 2,
        repeat: Infinity,
        ease: 'easeInOut',
      }
    },
  } as Variants,
} as const

// ============================================
// HEADER & CONTENT ANIMATIONS
// ============================================

export const contentAnimations = {
  // Header section
  header: {
    hidden: { opacity: 0, y: -15 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.4,
        ease: easings.easeOutSmooth,
      }
    },
  } as Variants,
  
  // Content section
  section: {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: transitions.normal
    },
  } as Variants,
  
  // Grid item
  gridItem: {
    hidden: { opacity: 0, y: 15, scale: 0.98 },
    visible: { 
      opacity: 1, 
      y: 0, 
      scale: 1,
      transition: {
        duration: 0.3,
        ease: easings.easeSpring,
      }
    },
  } as Variants,
} as const
